/*
 * auth-react 0.9's useAuth().authState and useUserClaims(), rebuilt on auth-react 1.x (needed by
 * auth-js 2.x, which dropped both), so callers only change an import. Both read the auth state
 * auth-js keeps on Okta's auth state manager; a token imported from the Forms widget has no ID token.
 */
import { useEffect, useMemo, useState } from 'react';
import type { AccessToken, AuthState, IdToken, UserClaims, UserManager } from '@skyslope/auth-js';
import { useAuth as useAuthReact } from '@skyslope/auth-react';

// Token claims as auth-react 0.9 typed them (from Okta 5): the known claims plus any others.
type TokenClaims = { sub: string; [name: string]: any };

type CompatAuthState = Omit<AuthState, 'accessToken' | 'idToken'> & {
  accessToken?: Omit<AccessToken, 'claims'> & { claims: TokenClaims };
  idToken?: Omit<IdToken, 'claims'> & { claims: TokenClaims };
};

// What auth-react 0.9 returned before the first auth state arrived.
const DEFAULT_AUTH_STATE: CompatAuthState = {
  isAuthenticated: false,
  idToken: undefined,
  accessToken: undefined,
};

function readAuthState(userManager: UserManager | undefined): CompatAuthState {
  return (
    (userManager?._oktaAuth?.oktaAuth?.authStateManager?.getAuthState() as CompatAuthState | null) ?? DEFAULT_AUTH_STATE
  );
}

/**
 * Same shape as auth-react 0.9's useAuth(). auth-react 1.x re-renders its consumers on every Okta
 * auth state change, so reading the state during render stays current. Okta replaces the state
 * object on each change, so its identity is stable between changes, as it was in 0.9.
 */
export function useAuth() {
  const { userManager, userContext } = useAuthReact();
  const authState = readAuthState(userManager);
  return { userManager, userContext, authState };
}

/** Same as auth-react 0.9's useUserClaims(): the ID token claims, kept once seen. */
export function useUserClaims(): { claims?: UserClaims; isLoading: boolean } {
  const { authState } = useAuth();
  const [claims, setClaims] = useState<UserClaims>();

  useEffect(() => {
    const idTokenClaims = authState.idToken?.claims as UserClaims | undefined;
    if (authState.isAuthenticated && idTokenClaims) setClaims(idTokenClaims);
  }, [authState.isAuthenticated, authState.idToken]);

  return useMemo(() => ({ claims, isLoading: claims == null }), [claims]);
}
